const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function greenDominance(red: number, green: number, blue: number): number {
  return green - Math.max(red, blue);
}

function isGreenPixelAt(data: Uint8ClampedArray, width: number, x: number, y: number): boolean {
  const offset = (y * width + x) * 4;
  const red = data[offset]!;
  const green = data[offset + 1]!;
  const blue = data[offset + 2]!;
  return green >= 120 && greenDominance(red, green, blue) >= 45;
}

/** Returns true only when three image corners contain a clear green key field. */
export function hasGreenScreenCorners(data: Uint8ClampedArray, width: number, height: number): boolean {
  if (width < 8 || height < 8 || data.length !== width * height * 4) return false;
  const patch = Math.max(1, Math.floor(Math.min(width, height) * 0.012));
  const insetX = Math.max(patch, Math.floor(width * 0.025));
  const insetY = Math.max(patch, Math.floor(height * 0.025));
  const corners = [
    [insetX, insetY],
    [width - insetX - 1, insetY],
    [insetX, height - insetY - 1],
    [width - insetX - 1, height - insetY - 1],
  ];
  let greenCorners = 0;
  for (const [centerX, centerY] of corners) {
    let greenSamples = 0;
    let samples = 0;
    for (let y = Math.max(0, centerY! - patch); y <= Math.min(height - 1, centerY! + patch); y += 1) {
      for (let x = Math.max(0, centerX! - patch); x <= Math.min(width - 1, centerX! + patch); x += 1) {
        samples += 1;
        if (isGreenPixelAt(data, width, x, y)) greenSamples += 1;
      }
    }
    if (samples && greenSamples / samples >= 0.72) greenCorners += 1;
  }
  return greenCorners >= 3;
}

/** Chroma keys a pure green-screen image, retaining antialiased edges and reducing green spill. */
export function removeBloodCrimesGreenScreen(data: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  if (!hasGreenScreenCorners(data, width, height)) throw new Error('Green screen not detected in at least three corners; image left unchanged.');
  const output = new Uint8ClampedArray(data);
  for (let offset = 0; offset < output.length; offset += 4) {
    const red = output[offset]!;
    const green = output[offset + 1]!;
    const blue = output[offset + 2]!;
    const keyAmount = smoothstep(18, 112, greenDominance(red, green, blue)) * smoothstep(70, 145, green);
    if (keyAmount <= 0) continue;
    const originalAlpha = output[offset + 3]! / 255;
    const alpha = originalAlpha * (1 - keyAmount);
    if (alpha <= 0.012) {
      output[offset] = 0;
      output[offset + 1] = 0;
      output[offset + 2] = 0;
      output[offset + 3] = 0;
      continue;
    }
    const safeAlpha = Math.max(0.08, 1 - keyAmount);
    output[offset] = clamp((red - 0 * keyAmount) / safeAlpha, 0, 255);
    output[offset + 1] = clamp((green - 255 * keyAmount) / safeAlpha, 0, 255);
    output[offset + 2] = clamp((blue - 0 * keyAmount) / safeAlpha, 0, 255);
    output[offset + 3] = Math.round(alpha * 255);
  }
  return output;
}

export async function createBloodCrimesGreenScreenPng(source: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Could not initialize the local image editor.');
    context.drawImage(bitmap, 0, 0);
    const pixels = context.getImageData(0, 0, bitmap.width, bitmap.height);
    pixels.data.set(removeBloodCrimesGreenScreen(pixels.data, bitmap.width, bitmap.height));
    context.putImageData(pixels, 0, 0);
    const output = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!output) throw new Error('Could not encode the transparent PNG.');
    return output;
  } finally {
    bitmap.close();
  }
}
